import { useCallback, useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { DEMO_CHAT } from "./mdtDemo";

/* Dispatch text channel — slide-over drawer on the MDT. Units off duty (10-7)
   catch messages as phone texts. */

export default function ChatDrawer({ serverId, token, demo, open, onClose, onSeen }) {
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(() => {
    if (demo) { setMsgs(DEMO_CHAT); return; }
    api.get(`/nc/servers/${serverId}/cad/chat`, { params: { token } })
      .then((r) => { setMsgs(r.data.messages); onSeen?.(r.data.messages.length); }).catch(() => {});
  }, [demo, serverId, token, onSeen]);

  useEffect(() => {
    if (!open) return;
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [open, load]);

  useEffect(() => { if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length, open]);

  const send = async () => {
    if (!draft.trim()) return;
    if (demo) { setMsgs((m) => [...m, { id: `l${Date.now()}`, author: "1-ADAM-12", body: draft.trim(), at: new Date().toISOString() }]); setDraft(""); return; }
    setBusy(true);
    try { await api.post(`/nc/servers/${serverId}/cad/chat`, { body: draft.trim() }, { params: { token } }); setDraft(""); load(); }
    catch { /* next poll */ } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="absolute top-[72px] right-0 bottom-0 w-[300px] z-30 border-l border-t border-white/15 bg-[#0a0d12]/95 backdrop-blur flex flex-col" data-testid="chat-drawer">
      <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-white/10">
        <MessageSquare className="w-3.5 h-3.5 text-sky-400" />
        <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-300">Dispatch channel</p>
        <button data-testid="chat-close" onClick={onClose} className="ml-auto text-zinc-500 hover:text-zinc-200"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2" style={{ scrollbarWidth: "thin" }}>
        {msgs.map((m) => (
          <div key={m.id} className="flex flex-col gap-0.5">
            <p className="font-mono text-[7.5px] uppercase tracking-[0.15em] text-zinc-500">{m.author} · {String(m.at).slice(11, 16)}</p>
            <p className="font-mono text-[10px] text-zinc-200 leading-snug border border-white/10 bg-black/40 px-2.5 py-1.5 w-fit max-w-full">{m.body}</p>
          </div>
        ))}
        {msgs.length === 0 && <p className="font-mono text-[8px] text-zinc-600 uppercase tracking-[0.15em] text-center py-6">Channel quiet — say something</p>}
        <div ref={bottomRef} />
      </div>
      <div className="p-2.5 border-t border-white/10 flex gap-1.5">
        <input data-testid="chat-input" value={draft} maxLength={300} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="All units…" className="flex-1 min-w-0 bg-black/60 border border-white/15 px-2.5 py-2 font-mono text-[10px] text-zinc-100 focus:outline-none focus:ring-1 focus:ring-sky-500" />
        <button data-testid="chat-send" onClick={send} disabled={busy || !draft.trim()} className="px-2.5 border border-sky-500/50 text-sky-300 hover:bg-sky-500/10 transition-colors disabled:opacity-40">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}
